/**
 * PaperSpaceLayer — Three.js component rendering the virtual paper sheet
 * when the user is in a layout (paper-space) tab.
 *
 * Renders the white PlaneGeometry for the paper sheet body.
 * The printable-area margin frame is intentionally NOT rendered here —
 * it stacked near-coincident with the VP frame border and created a fat
 * grey band (gap2-iter7 user directive: «отступ убрать»; AutoCAD shows
 * the viewport border as the only visible boundary, not the margin inset).
 *
 * The paper sheet sits at Z = -0.5 so it renders behind all model
 * entities (which live at Z = 0).  Background colour of the Canvas
 * stays at the dark CAD grey; the white plane provides the paper
 * contrast without changing the global clear colour.
 *
 * Dimensions are in world units (mm for metric drawings, inches for
 * imperial — matching the DXF plotPaperUnits convention from the
 * extracted LayoutPageSetup).
 *
 * Coordinate frame: the sheet is placed at the `origin` PROP, which is the
 * layout's `LayoutPageSetup.origin` already translated into the renderer's
 * OFFSET-world frame by the caller. It is NOT `pageSetup.origin`: every
 * geometry adapter subtracts the locked session origin from every coordinate
 * (paper-space entities included), so a sheet placed at the raw origin sits a
 * whole session origin away from the title block drawn on it — a blank white
 * page on any drawing whose origin is non-zero. See
 * `scene3d/coordinateOrigin.ts applyOffsetToPaperOrigin`.
 *
 * The paper sheet is ALWAYS the full physical sheet — the plot Window
 * (PLOT area = Window) is a print-area indicator only (shown via the
 * `<PrintPreviewBounds>` overlay while the Plot dialog is open) and does
 * NOT resize the working sheet (AutoCAD parity: the layout sheet stays
 * full so viewports / geometry remain editable; only the PLOT output is
 * clipped to the window). The picked window lives on the layout's
 * `plotSettings` and is consumed by the PDF exporter, not by this render.
 */

import * as React from "react";
import * as THREE from "three";

import type { LayoutPageSetup } from "@dxf-ai-editor/drawing-model";

import { PAPER_SPACE_BACKGROUND_HEX } from "../rendering/layoutForeground";

export interface PaperSpaceLayerProps {
  pageSetup: LayoutPageSetup;
  /**
   * Lower-left corner of the sheet in the renderer's OFFSET-world frame —
   * `coordinateOrigin.applyOffsetToPaperOrigin(pageSetup.origin)`, resolved
   * ONCE by `Scene3DStage` and threaded to every consumer of the sheet
   * origin.
   *
   * Deliberately a PROP rather than a conversion inside this component: the
   * VP frame layer, the VP scissor rects, the paper↔model transform and the
   * pointer hit-tests all place themselves from the same value, and a second
   * in-component conversion would be a second place for that value to be
   * derived (CLAUDE.md PRIORITY #2.1 — one authoritative derivation).
   *
   * Required, not defaulted: silently falling back to `pageSetup.origin`
   * would reinstate the raw-frame sheet this prop exists to eliminate.
   */
  origin: { x: number; y: number };
}

const PAPER_Z = -0.5;

/**
 * Opaque white material for the paper sheet.
 *
 * The colour comes from `rendering/colorResolution.ts` because it is HALF of a
 * pair: this is the surface AutoCAD colour 7 contrasts against, and that module
 * derives the matching foreground (black) from the same constant. Drifting the
 * two apart is exactly the defect that made an on-sheet title block invisible.
 */
const SHEET_MATERIAL = new THREE.MeshBasicMaterial({
  color: new THREE.Color(PAPER_SPACE_BACKGROUND_HEX),
  side: THREE.FrontSide,
  depthWrite: false,
});

export function PaperSpaceLayer({
  pageSetup,
  origin,
}: PaperSpaceLayerProps): React.ReactElement {
  const { sheetSize } = pageSetup;

  const sheetGeo = React.useMemo(() => {
    return new THREE.PlaneGeometry(sheetSize.widthMm, sheetSize.heightMm);
  }, [sheetSize.widthMm, sheetSize.heightMm]);
  React.useEffect(() => () => { sheetGeo.dispose(); }, [sheetGeo]);

  return (
    <group position={[origin.x, origin.y, 0]} userData={{ isViewportOverlay: true }}>
      {/* Paper sheet — white plane at Z=-0.5 (always the full physical sheet). */}
      <mesh
        geometry={sheetGeo}
        material={SHEET_MATERIAL}
        position={[sheetSize.widthMm / 2, sheetSize.heightMm / 2, PAPER_Z]}
        renderOrder={-10}
      />
    </group>
  );
}
